/**
 * Root component for the Alterspective Advice task pane.
 *
 * When not authenticated, shows the LoginPanel.
 * When authenticated, shows the branded header with user info,
 * tab navigation across five panels, and a connection status footer.
 */
import React, { useState, useEffect } from 'react';
import {
  FluentProvider,
  webLightTheme,
  TabList,
  Tab,
  SelectTabEvent,
  SelectTabData,
  Badge,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkCircle20Regular } from '@fluentui/react-icons/svg/checkmark-circle';
import { ChevronDown16Regular } from '@fluentui/react-icons/svg/chevron-down';
import { DocumentBriefcase20Regular } from '@fluentui/react-icons/svg/document-briefcase';
import { Flow20Regular } from '@fluentui/react-icons/svg/flow';
import { PersonAccount16Regular } from '@fluentui/react-icons/svg/person-account';
import { SignOut20Regular } from '@fluentui/react-icons/svg/sign-out';
import { TableCalculator20Regular } from '@fluentui/react-icons/svg/table-calculator';
import { useAuth } from '../auth/auth-context';
import { getDemoMatterId } from '../auth/auth-service';
import { LoginPanel } from './panels/LoginPanel';
import { MatterPanel } from './panels/MatterPanel';
import { ReservePanel } from './panels/ReservePanel';
import { InsertPanel } from './panels/InsertPanel';
import { StatusPanel } from './panels/StatusPanel';
import { FinalisePanel } from './panels/FinalisePanel';
import { DelegationPanel } from './panels/DelegationPanel';

declare const __DEMO_SAFE_TABS__: boolean | undefined;
const DEMO_SAFE_TABS = typeof __DEMO_SAFE_TABS__ !== 'undefined' && __DEMO_SAFE_TABS__ === true;
import { AdviceHistoryPanel } from './panels/AdviceHistoryPanel';
import { InsightsPanel } from './panels/InsightsPanel';
import { readMatterLink } from '../office/document-metadata';
import type { DocumentLink } from '@alterspective/shared';

/** Tab identifiers for the task pane navigation. */
type TabId = 'matter' | 'reserves' | 'insert' | 'insights' | 'workflow' | 'history' | 'status' | 'finalise';

const BRAND_COLOR = '#075156';
const DEMO_MATTER_ID = getDemoMatterId();

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    fontFamily: tokens.fontFamilyBase,
  },
  header: {
    backgroundColor: BRAND_COLOR,
    color: '#ffffff',
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
  },
  brandBlock: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  headerTitle: {
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '0',
    lineHeight: '18px',
  },
  headerSubtitle: {
    fontSize: '11px',
    lineHeight: '14px',
    color: 'rgba(255,255,255,0.76)',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  accountButton: {
    color: '#ffffff',
    minWidth: 0,
    maxWidth: '150px',
    padding: '2px 6px',
    '&:hover': {
      color: '#ffffff',
      backgroundColor: 'rgba(255,255,255,0.12)',
    },
  },
  accountButtonInner: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0,
  },
  accountName: {
    fontSize: '11px',
    color: '#ffffff',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  accountMenuHeader: {
    padding: '10px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  accountMenuName: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  accountMenuMeta: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    maxWidth: '240px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  flowStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '6px',
    padding: '8px 10px',
    backgroundColor: '#f6fbfa',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  flowStep: {
    minWidth: 0,
    minHeight: '46px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 7px',
    cursor: 'pointer',
    fontFamily: tokens.fontFamilyBase,
    textAlign: 'left',
    '&:hover': {
      border: `1px solid ${BRAND_COLOR}`,
      backgroundColor: '#eef8f7',
    },
    '&:focus-visible': {
      outline: `2px solid ${BRAND_COLOR}`,
      outlineOffset: '2px',
    },
  },
  flowStepActive: {
    border: `1px solid ${BRAND_COLOR}`,
    backgroundColor: '#e7f4f2',
    color: BRAND_COLOR,
  },
  flowStepDone: {
    border: `1px solid ${tokens.colorPaletteGreenBorder2}`,
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  flowIcon: {
    flexShrink: 0,
    fontSize: '18px',
  },
  flowText: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  flowLabel: {
    fontSize: '11px',
    fontWeight: 700,
    lineHeight: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  flowDetail: {
    fontSize: '10px',
    lineHeight: '13px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tabBar: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    overflowX: 'auto',
    scrollbarWidth: 'thin',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '12px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  devBadge: {
    backgroundColor: '#ffc107',
    color: '#000',
    fontSize: '10px',
    fontWeight: 600,
    padding: '1px 6px',
    borderRadius: '4px',
    marginLeft: 'auto',
  },
});

/** Root application component for the Alterspective Instruct task pane. */
export const App: React.FC = () => {
  const styles = useStyles();
  const { isAuthenticated, user, isDevMode, logout } = useAuth();
  const [selectedTab, setSelectedTab] = useState<TabId>('matter');
  const [matterId, setMatterId] = useState<string | null>(DEMO_MATTER_ID || null);
  const [documentLink, setDocumentLink] = useState<DocumentLink | null>(null);
  const displayName = user?.displayName ?? 'Account';
  const accountMeta = user?.email ?? 'Authenticated Sharedo session';
  const hasMatterContext = !!matterId || !!documentLink;

  const workflowSteps: Array<{
    tab: TabId;
    label: string;
    detail: string;
    done: boolean;
    icon: React.ReactNode;
  }> = [
    {
      tab: 'matter',
      label: 'Matter',
      detail: hasMatterContext ? 'Linked' : 'Select',
      done: hasMatterContext,
      icon: <DocumentBriefcase20Regular />,
    },
    {
      tab: 'reserves',
      label: 'Reserves',
      detail: hasMatterContext ? 'Review' : 'Waiting',
      done: false,
      icon: <TableCalculator20Regular />,
    },
    {
      tab: 'finalise',
      label: 'Finalise',
      detail: 'Submit',
      done: false,
      icon: <CheckmarkCircle20Regular />,
    },
  ];

  // Read the matter link from the document on mount (pre-populates matterId)
  useEffect(() => {
    if (!isAuthenticated) return;
    readMatterLink().then((link) => {
      if (link) {
        setDocumentLink(link);
        if (!matterId) setMatterId(link.matterId);
      }
    }).catch(() => {
      // Non-fatal — document may not have a link yet
    });
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as TabId);
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <div className={styles.root}>
        {!isAuthenticated ? (
          <LoginPanel />
        ) : (
          <>
            <div className={styles.header}>
              <div className={styles.brandBlock}>
                <span className={styles.headerTitle}>Alterspective Instruct</span>
                <span className={styles.headerSubtitle}>Advice workflow</span>
              </div>
              <div className={styles.headerRight}>
                <Menu positioning="below-end">
                  <MenuTrigger disableButtonEnhancement>
                    <Button
                      className={styles.accountButton}
                      appearance="transparent"
                      size="small"
                      title={`Signed in as ${displayName}`}
                    >
                      <span className={styles.accountButtonInner}>
                        <PersonAccount16Regular />
                        <span className={styles.accountName}>{displayName}</span>
                        <ChevronDown16Regular />
                      </span>
                    </Button>
                  </MenuTrigger>
                  <MenuPopover>
                    <div className={styles.accountMenuHeader}>
                      <span className={styles.accountMenuName}>{displayName}</span>
                      <span className={styles.accountMenuMeta}>{accountMeta}</span>
                    </div>
                    <MenuList>
                      <MenuItem icon={<SignOut20Regular />} onClick={logout}>
                        Sign out
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              </div>
            </div>

            <div className={styles.flowStrip} aria-label="Advice workflow progress">
              {workflowSteps.map((step) => {
                const isActive = selectedTab === step.tab;
                return (
                  <button
                    key={step.tab}
                    type="button"
                    className={[
                      styles.flowStep,
                      isActive ? styles.flowStepActive : '',
                      step.done && !isActive ? styles.flowStepDone : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => setSelectedTab(step.tab)}
                    aria-current={isActive ? 'step' : undefined}
                    title={`${step.label}: ${step.detail}`}
                  >
                    <span className={styles.flowIcon}>{step.done && !isActive ? <Flow20Regular /> : step.icon}</span>
                    <span className={styles.flowText}>
                      <span className={styles.flowLabel}>{step.label}</span>
                      <span className={styles.flowDetail}>{step.detail}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            <TabList
              className={styles.tabBar}
              selectedValue={selectedTab}
              onTabSelect={onTabSelect}
              size="small"
              aria-label="Advice workflow sections"
            >
              <Tab value="matter">Matter</Tab>
              <Tab value="reserves">Reserves</Tab>
              <Tab value="insert">Insert</Tab>
              <Tab value="insights">Insights</Tab>
              {!DEMO_SAFE_TABS && <Tab value="workflow">Workflow</Tab>}
              {!DEMO_SAFE_TABS && <Tab value="history">History</Tab>}
              {!DEMO_SAFE_TABS && <Tab value="status">Status</Tab>}
              <Tab value="finalise">Finalise</Tab>
            </TabList>

            <div className={styles.content}>
              {selectedTab === 'matter' && (
                <MatterPanel
                  matterId={matterId}
                  onMatterIdChange={setMatterId}
                  existingLink={documentLink}
                  onLinkChange={setDocumentLink}
                />
              )}
              {selectedTab === 'reserves' && <ReservePanel matterId={matterId} />}
              {selectedTab === 'insert' && <InsertPanel matterId={matterId} />}
              {selectedTab === 'insights' && <InsightsPanel matterId={matterId} />}
              {!DEMO_SAFE_TABS && selectedTab === 'workflow' && <DelegationPanel matterId={matterId} />}
              {!DEMO_SAFE_TABS && selectedTab === 'history' && <AdviceHistoryPanel matterId={matterId} />}
              {!DEMO_SAFE_TABS && selectedTab === 'status' && <StatusPanel />}
              {selectedTab === 'finalise' && <FinalisePanel matterId={matterId} />}
            </div>

            <div className={styles.footer}>
              <Badge
                appearance="filled"
                color="success"
                size="tiny"
              />
              <span>Connected</span>
              {isDevMode && <span className={styles.devBadge}>DEV MODE</span>}
            </div>
          </>
        )}
      </div>
    </FluentProvider>
  );
};
